import { h, render } from 'preact';
import { App } from '@authentic/loyalty-pwa';
import { GlobalStyle, route } from '@authentic/loyalty-pwa/util';
import { CheetahService, Events, EventsService } from '@authentic/loyalty-pwa/services';
import { AuthenticatedRoute, ExecRoute, Redirect, Router } from '@authentic/loyalty-pwa/components';
import { LoginResult, SignupResult } from '@authentic/loyalty-pwa/interfaces';
import { UserProvider } from '@authentic/loyalty-pwa/providers';
import { oauthServices } from './lib/OAuthServices'
import { UserData } from '@authentic/loyalty-pwa/model';
import { DelYeahAboutProgram } from './pages/del-yeah/about-program/about-program';
import { DelYeahHomePage } from './pages/del-yeah/HomePage';

import { EmailVerified } from './pages/EmailVerified';
import { DelYeahScanCode } from './pages/del-yeah/scan-code/scan-code';
import { DelYeahYourRewards } from './pages/del-yeah/your-rewards/your-rewards';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { Registration } from './pages/Registration';
import { SignInPage } from './pages/SignIn';
import { VerifyEmail } from './pages/VerifyEmail';
import { Route, RouterOnChangeArgs } from 'preact-router';
import { ChangePasswordPage } from './pages/change-password/change-password';
import { DelYeahEnterCoupon } from './pages/del-yeah/enter-coupon/enter-coupon';
import { ContentPage } from './pages/content-page/content-page';
import { OrderRedirect } from './lib/OrderRedirect';
import { DelYeahViewHistory } from './pages/del-yeah/my-history/view-history/view-history';
import { DelYeahMyHistory } from './pages/del-yeah/my-history/my-history';
import { ChangePasswordProfilePage } from './pages/del-yeah/profile/change-password/change-password';
import { DelYeahProfileAndSettings } from './pages/del-yeah/profile/profile-settings';
import { DelYeahEditProfilePage } from './pages/del-yeah/profile/edit-profile/edit-profile';
import { DelYeahEnterReceipt } from './pages/del-yeah/enter-receipt/enter-receipt';

const cheetahService = new CheetahService({
  base_url: process.env.CHEETAH_BASE_URL || '',
  client_id: process.env.CHEETAH_CLIENT_ID || '',
  client_secret: process.env.CHEETAH_CLIENT_SECRET || '',
  oauth_services: oauthServices
})

EventsService.on(Events.USER_SSO).when((ssoResponse: Partial<SignupResult> & Partial<LoginResult>) => {
  if (ssoResponse.access_token)
    return
  // When we get an SSO event but it wasn't succesful, we're triggering registration
  const user = UserProvider.get()

  if (ssoResponse.name === 'require_confirmation')
    return route('/email-verify')

  UserProvider.update({appdata: {
    ...user.appdata,
    sso_fields: ssoResponse?.member,
    sso_registration: true
  }, redirect: '/sign-up'})
})

EventsService.on(Events.SSO_RESPONSE).when(({access_token, state}) => {
  const user = UserProvider.get()
  // If we get SSO response, save it in case the registration page needs it
  console.log('Storing SSO response', access_token, state)
  UserProvider.update({appdata: {
    ...user.appdata,
    sso: {
      token: access_token,
      provider: state
    }
  }})
})

EventsService.on(Events.USER_CHANGED).when((user: UserData) => {
  if (user.redirect) {
    const redirect = user.redirect
    UserProvider.update({redirect: false, redirect_after_login: false})
    route(redirect)
  }
})
// If user logs out, go ahead and take them to root and let our router handle it
EventsService.on(Events.USER_LOGOUT).then(() => { route('/') })

// Scroll to the top when changing routes
async function handleRoute(e: RouterOnChangeArgs) {
  if (e.previous !== e.url) {
    window.scrollTo(0, 0);
  }
}

render(
  <App receiptService={cheetahService} userService={cheetahService} locationService={cheetahService} rewardsService={cheetahService} offersService={cheetahService} contentPageService={cheetahService} historyService={cheetahService}>
    <GlobalStyle />
    <Router onChange={handleRoute}>
      <Route default path="/404" component={ContentPage} />
      <AuthenticatedRoute invert path="/sign-in" component={SignInPage} fallback="/" />
      <AuthenticatedRoute invert path="/sign-up" component={Registration} fallback="/" />
      <AuthenticatedRoute invert path="/forgot-password" component={ForgotPasswordPage} fallback="/" />
      <AuthenticatedRoute invert path="/email-verify" component={VerifyEmail} fallback="/" />
      <Route path="/change-password" component={ChangePasswordPage.bind(this)} />
      <Route path="/email-verified" component={EmailVerified} />
      <AuthenticatedRoute path="/" component={DelYeahHomePage} fallback="/sign-in" />
      <AuthenticatedRoute path="/your-rewards" component={DelYeahYourRewards} fallback="/sign-in" />
      <AuthenticatedRoute path="/scan-code" component={DelYeahScanCode} fallback="/sign-in" />
      <AuthenticatedRoute path="/view-history" component={DelYeahViewHistory} fallback="/sign-in" />
      <AuthenticatedRoute path="/your-history" component={DelYeahMyHistory} fallback="/sign-in" />
      <AuthenticatedRoute path="/profile" component={DelYeahProfileAndSettings} fallback="/sign-in" />
      <AuthenticatedRoute path="/about" component={DelYeahAboutProgram} fallback="/sign-in" />
      <AuthenticatedRoute path="/enter-receipt" component={DelYeahEnterReceipt} fallback="/sign-in" />
      <AuthenticatedRoute path="/enter-coupon" component={DelYeahEnterCoupon.bind(this)} fallback="/sign-in?enter_coupon=true" />
      <AuthenticatedRoute path="/change-password-profile" component={ChangePasswordProfilePage} fallback="/sign-in" />
      <AuthenticatedRoute path="/edit-profile" component={DelYeahEditProfilePage} fallback="/sign-in" />
      <ExecRoute path="/order" exec={OrderRedirect} />
      <ExecRoute path="/sign-out" exec={UserProvider.logout} />
    </Router>
  </App>,
  document.body,
);
